/**
 * The city's own edits (admin doc 12 §3.2): a new city, its identity, its localities and its
 * travel rings. Split from `Cities.tsx`, which keeps the list, the lifecycle and service areas.
 *
 * **A city hangs off a district.** The new-city form picks a state, then one of its districts,
 * and can add the district inline — a district is reference data ops maintain, not a free-text
 * field on the city.
 *
 * **Coordinates are checked against India's bounding box before they are sent.** The server is
 * still the boundary; the client check only saves a round trip on a swapped latitude/longitude,
 * which is the commonest typo and puts Patna in the Indian Ocean.
 *
 * **A ring's bounds are history once a quote has used them.** Rings are added and retired here,
 * never re-drawn: the patch endpoint takes a label and an active flag only, and the legal way to
 * move a boundary is to retire the ring and add its successor. A gap between rings is allowed and
 * the server's warning is shown as it said it.
 */
import { useState } from 'react';
import { Kicker, Note, StatusChip } from '../components/primitives';
import { KV } from '../components/entities';
import { Input, Select, Toggle } from '../components/forms';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { useConsole } from '../app/console';
import { ApiError, errorMessage } from '../lib/api/client';
import {
  useCreateCity,
  useCreateDistrict,
  useCreateLocality,
  useCreateTravelZone,
  useDistricts,
  useLocalities,
  usePatchCity,
  usePatchLocality,
  usePatchServiceArea,
  useDeleteLocality,
  usePatchTravelZone,
  useStates,
  useCityWaitlist,
  useTravelZones,
  type CityPatchIn,
  type CityRow,
  type LocalityRow,
  type ServiceAreaRow,
  type TravelZoneRow,
} from '../lib/api/hooks/geo';

const DECIMAL = /^-?\d{1,3}(\.\d{1,7})?$/;
const KM = /^\d{1,4}(\.\d)?$/;
const PINCODE = /^\d{6}$/;

/** In-India bounds, generously: a latitude of 85 is a swapped pair, not a city. */
export function coordinateError(raw: string, axis: 'lat' | 'lng'): string | undefined {
  if (!DECIMAL.test(raw.trim())) return 'A decimal, e.g. 25.5941';
  const value = Number(raw);
  const [lo, hi] = axis === 'lat' ? [6, 38] : [68, 98];
  return value >= lo && value <= hi ? undefined : `Outside India (${lo}–${hi})`;
}

const wholeError = (raw: string, max: number) =>
  /^\d+$/.test(raw) && Number(raw) <= max ? undefined : `A whole number up to ${max}`;

function Buttons({ label, disabled, onSave, onCancel }: { label: string; disabled: boolean; onSave: () => void; onCancel: () => void }) {
  const { scope } = useConsole();
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={disabled} onClick={onSave}>
        {label}
      </ScopeAction>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </ScopeAction>
    </div>
  );
}

/* ── New city ───────────────────────────────────────────────────────────────────────────── */

export function NewCityForm({ onDone }: { onDone: () => void }) {
  const { scope, toast } = useConsole();
  const states = useStates();
  const [stateName, setStateName] = useState('');
  const stateId = states.data?.items.find((s) => s.name === stateName)?.id;
  const districts = useDistricts(stateId);
  const [districtName, setDistrictName] = useState('');
  const [newDistrict, setNewDistrict] = useState('');
  const [name, setName] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState('25');
  const [metro, setMetro] = useState(false);
  const createDistrict = useCreateDistrict();
  const create = useCreateCity();
  const districtId = districts.data?.find((d) => d.name === districtName)?.id;
  const latError = lat === '' ? undefined : coordinateError(lat, 'lat');
  const lngError = lng === '' ? undefined : coordinateError(lng, 'lng');
  const radiusError = wholeError(radius, 100);
  const valid =
    districtId !== undefined && name.trim() !== '' && lat !== '' && lng !== '' && !latError && !lngError && !radiusError;

  return (
    <Section title="New city">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Select
          label="State"
          placeholder="Choose a state"
          options={(states.data?.items ?? []).map((s) => s.name)}
          value={stateName}
          onChange={(next) => {
            setStateName(next);
            setDistrictName('');
          }}
        />
        <Select
          label="District"
          placeholder={stateId === undefined ? 'Choose a state first' : 'Choose a district'}
          options={(districts.data ?? []).map((d) => d.name)}
          value={districtName}
          disabled={stateId === undefined}
          onChange={setDistrictName}
        />
        {stateId !== undefined && (
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end' }}>
            <Input label="New district" value={newDistrict} onChange={setNewDistrict} helper="Not in the list? Add it to this state" />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="secondary"
              disabled={newDistrict.trim() === '' || createDistrict.isPending}
              onClick={() =>
                createDistrict.mutate(
                  { stateId, name: newDistrict.trim() },
                  {
                    onSuccess: (d) => {
                      setDistrictName(d.name);
                      setNewDistrict('');
                      toast({ tone: 'success', message: `District ${d.name} added` });
                    },
                  },
                )
              }
            >
              Add district
            </ScopeAction>
          </div>
        )}
        {createDistrict.isError && (
          <ErrorState inline title="The server refused this district" cause={errorMessage(createDistrict.error)} />
        )}
        <Input label="City name" value={name} onChange={setName} helper="The slug is minted from it and never changes" />
        <Input label="Hindi name" value={nameHi} onChange={setNameHi} helper="Optional, Devanagari" />
        <Input label="Centre latitude" value={lat} onChange={setLat} inputMode="decimal" error={latError} />
        <Input label="Centre longitude" value={lng} onChange={setLng} inputMode="decimal" error={lngError} />
        <Input label="Radius km" value={radius} onChange={setRadius} inputMode="numeric" error={radiusError} />
        <Toggle label="Metro" on={metro} onChange={setMetro} />
        {create.isError && <ErrorState inline title="The server refused this city" cause={errorMessage(create.error)} />}
        <Buttons
          label="Create city"
          disabled={!valid || create.isPending}
          onCancel={onDone}
          onSave={() =>
            create.mutate(
              {
                district_id: districtId!,
                name: name.trim(),
                name_hi: nameHi.trim() || null,
                latitude: lat.trim(),
                longitude: lng.trim(),
                radius_km: Number(radius),
                is_metro: metro,
              },
              {
                onSuccess: (city) => {
                  toast({ tone: 'success', message: `${city.name} created · not planned until a stage is chosen` });
                  onDone();
                },
              },
            )
          }
        />
      </div>
    </Section>
  );
}

/* ── Identity ───────────────────────────────────────────────────────────────────────────── */

export function CityIdentityForm({ city, onDone }: { city: CityRow; onDone: () => void }) {
  const { toast } = useConsole();
  const patch = usePatchCity();
  const [name, setName] = useState(city.name);
  const [nameHi, setNameHi] = useState(city.name_hi ?? '');
  const [lat, setLat] = useState(String(city.latitude));
  const [lng, setLng] = useState(String(city.longitude));
  const [radius, setRadius] = useState(String(city.radius_km));
  const [metro, setMetro] = useState(city.is_metro);
  const [population, setPopulation] = useState(city.population === null || city.population === undefined ? '' : String(city.population));
  const [intro, setIntro] = useState(city.intro_text ?? '');
  const latError = coordinateError(lat, 'lat');
  const lngError = coordinateError(lng, 'lng');
  const radiusError = wholeError(radius, 100);
  const populationError = population === '' ? undefined : wholeError(population, 2_000_000_000);
  const valid = name.trim() !== '' && !latError && !lngError && !radiusError && !populationError;

  const save = () => {
    const candidate: CityPatchIn = {
      name: name.trim(),
      name_hi: nameHi.trim() || null,
      latitude: lat.trim(),
      longitude: lng.trim(),
      radius_km: Number(radius),
      is_metro: metro,
      population: population === '' ? null : Number(population),
      intro_text: intro.trim() || null,
    };
    // Coordinates compare as numbers: the server sends `25.5941000`, the operator types `25.5941`.
    const current: Record<string, unknown> = { ...city, latitude: Number(city.latitude), longitude: Number(city.longitude) };
    const changes = Object.fromEntries(
      Object.entries(candidate).filter(([k, v]) => {
        const comparable = k === 'latitude' || k === 'longitude' ? Number(v) : v;
        return (current[k] ?? null) !== comparable;
      }),
    ) as CityPatchIn;
    patch.mutate(
      { cityId: city.id, patch: changes },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${name.trim()} saved` });
          onDone();
        },
      },
    );
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
      <Input label="City name" value={name} onChange={setName} helper={`The slug stays ${city.slug}`} />
      <Input label="Hindi name" value={nameHi} onChange={setNameHi} />
      <Input label="Centre latitude" value={lat} onChange={setLat} inputMode="decimal" error={latError} />
      <Input label="Centre longitude" value={lng} onChange={setLng} inputMode="decimal" error={lngError} />
      <Input label="Radius km" value={radius} onChange={setRadius} inputMode="numeric" error={radiusError} />
      <Input label="Population" value={population} onChange={setPopulation} inputMode="numeric" error={populationError} />
      <Input label="Intro text" value={intro} onChange={setIntro} multiline helper="Shown on the city page; linted for outcome claims" />
      <Toggle label="Metro" on={metro} onChange={setMetro} />
      {patch.isError && <ErrorState inline title="The server refused this edit" cause={errorMessage(patch.error)} />}
      <Buttons label="Save city" disabled={!valid || patch.isPending} onSave={save} onCancel={onDone} />
    </div>
  );
}

/* ── Row editors ────────────────────────────────────────────────────────────────────────── */

/** Only the fields that differ from the row, so an untouched field is never re-sent. */
function changedFields<T extends Record<string, unknown>>(row: Record<string, unknown>, candidate: T): Partial<T> {
  return Object.fromEntries(Object.entries(candidate).filter(([k, v]) => (row[k] ?? null) !== v)) as Partial<T>;
}

/**
 * A service area's name, centre, radius and notes (admin doc 12 §3.2). Pausing stays its own
 * button on the row; this is for correcting what the area is.
 */
export function ServiceAreaEditForm({ area, onDone }: { area: ServiceAreaRow; onDone: () => void }) {
  const { toast } = useConsole();
  const patch = usePatchServiceArea();
  const [name, setName] = useState(area.name);
  const [lat, setLat] = useState(String(area.latitude));
  const [lng, setLng] = useState(String(area.longitude));
  const [radius, setRadius] = useState(String(area.radius_km));
  const [notes, setNotes] = useState(area.notes ?? '');
  const latError = coordinateError(lat, 'lat');
  const lngError = coordinateError(lng, 'lng');
  const radiusError = wholeError(radius, 100);
  const valid = name.trim() !== '' && !latError && !lngError && !radiusError;

  const save = () => {
    const changes = changedFields(area, { name: name.trim(), radius_km: Number(radius), notes: notes.trim() || null });
    // Coordinates compare as numbers: the server sends `25.5900000`, the operator types `25.59`.
    const centre = {
      ...(Number(lat) === Number(area.latitude) ? {} : { latitude: lat.trim() }),
      ...(Number(lng) === Number(area.longitude) ? {} : { longitude: lng.trim() }),
    };
    patch.mutate(
      { areaId: area.id, patch: { ...changes, ...centre } },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${name.trim()} saved` });
          onDone();
        },
      },
    );
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-sm) var(--sp-md)' }}>
      <Input label="Edit area name" value={name} onChange={setName} />
      <Input label="Edit latitude" value={lat} onChange={setLat} inputMode="decimal" error={latError} />
      <Input label="Edit longitude" value={lng} onChange={setLng} inputMode="decimal" error={lngError} />
      <Input label="Edit radius km" value={radius} onChange={setRadius} inputMode="numeric" error={radiusError} />
      <Input label="Notes" value={notes} onChange={setNotes} helper="For ops only — never shown to a devotee" />
      {patch.isError && <ErrorState inline title="The server refused this edit" cause={errorMessage(patch.error)} />}
      <Buttons label="Save area" disabled={!valid || patch.isPending} onSave={save} onCancel={onDone} />
    </div>
  );
}

function LocalityEditForm({ locality, onDone }: { locality: LocalityRow; onDone: () => void }) {
  const { toast } = useConsole();
  const patch = usePatchLocality();
  const [name, setName] = useState(locality.name);
  const [pincode, setPincode] = useState(locality.pincode ?? '');
  const pinError = pincode === '' || PINCODE.test(pincode) ? undefined : 'Six digits';
  const save = () =>
    patch.mutate(
      { localityId: locality.id, patch: changedFields(locality, { name: name.trim(), pincode: pincode || null }) },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${name.trim()} saved` });
          onDone();
        },
      },
    );
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-sm) var(--sp-md)' }}>
      <Input label="Edit locality name" value={name} onChange={setName} helper={`The slug stays ${locality.slug}`} />
      <Input label="Edit pincode" value={pincode} onChange={setPincode} inputMode="numeric" error={pinError} />
      {patch.isError && <ErrorState inline title="The server refused this edit" cause={errorMessage(patch.error)} />}
      <Buttons label="Save locality" disabled={name.trim() === '' || pinError !== undefined || patch.isPending} onSave={save} onCancel={onDone} />
    </div>
  );
}

/** A ring's label only: its bounds are history once a quote has used them. */
function ZoneLabelForm({ zone, onDone }: { zone: TravelZoneRow; onDone: () => void }) {
  const { toast } = useConsole();
  const patch = usePatchTravelZone();
  const [label, setLabel] = useState(zone.label);
  const save = () =>
    patch.mutate(
      { zoneId: zone.id, patch: { label: label.trim() } },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${label.trim()} saved` });
          onDone();
        },
      },
    );
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-sm) var(--sp-md)' }}>
      <Input label="New label" value={label} onChange={setLabel} helper="The bounds stay as they are" />
      {patch.isError && <ErrorState inline title="The server refused this edit" cause={errorMessage(patch.error)} />}
      <Buttons label="Save label" disabled={label.trim() === '' || label.trim() === zone.label || patch.isPending} onSave={save} onCancel={onDone} />
    </div>
  );
}

/* ── Localities ─────────────────────────────────────────────────────────────────────────── */

const rowStyle = {
  display: 'flex',
  gap: 'var(--sp-sm)',
  alignItems: 'center',
  padding: 'var(--sp-sm) var(--sp-md)',
  border: '1px solid var(--c-border)',
  borderRadius: 'var(--r-tile)',
} as const;

export function LocalitiesPanel({ cityId }: { cityId: number }) {
  const { scope, toast } = useConsole();
  const localities = useLocalities(cityId);
  const create = useCreateLocality();
  const patch = usePatchLocality();
  const remove = useDeleteLocality();
  const [deleting, setDeleting] = useState<{ id: number; name: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [pincode, setPincode] = useState('');
  const pinError = pincode === '' || PINCODE.test(pincode) ? undefined : 'Six digits';

  return (
    <Section title="Localities">
      <Note>What a devotee types when they say where they are. Retire one rather than lose it — addresses point at it.</Note>
      {localities.isPending && <LoadingState message="Reading localities…" />}
      {localities.isError && <ErrorState title="Could not read localities" cause={errorMessage(localities.error)} />}
      {localities.isSuccess && localities.data.length === 0 && <EmptyState icon="map-pin" title="No locality listed" inline />}
      <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
        {localities.data?.map((l) => (
          <div key={l.id}>
          <div style={rowStyle}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{l.name}</div>
              <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>{l.pincode ?? 'no pincode'}</div>
            </div>
            <StatusChip tone={l.is_active ? 'success' : 'neutral'} label={l.is_active ? 'active' : 'retired'} />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              ariaLabel={`Edit ${l.name}`}
              onClick={() => setEditingId(editingId === l.id ? null : l.id)}
            >
              Edit
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              onClick={() =>
                patch.mutate(
                  { localityId: l.id, patch: { is_active: !l.is_active } },
                  {
                    onSuccess: () => toast({ tone: 'success', message: `${l.name} ${l.is_active ? 'retired' : 'restored'}` }),
                    onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
                  },
                )
              }
            >
              {l.is_active ? 'Retire' : 'Restore'}
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              onClick={() => setDeleting({ id: l.id, name: l.name })}
            >
              Delete
            </ScopeAction>
          </div>
          {editingId === l.id && <LocalityEditForm locality={l} onDone={() => setEditingId(null)} />}
          </div>
        ))}
      </div>
      <ConfirmAction
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ''}?`}
        phrase="DELETE"
        tone="error"
        confirmLabel="Delete"
        consequences={[
          'The row is removed for good, not retired. Only a locality nothing uses can go.',
          'If an address, a booking or a temple still names it, the server refuses and says which — retire it instead.',
        ]}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const target = deleting!;
          setDeleting(null);
          remove.mutate(target.id, {
            onSuccess: () => toast({ tone: 'success', message: `${target.name} deleted` }),
            onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
          });
        }}
      />
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
        <Input label="Locality name" value={name} onChange={setName} />
        <Input label="Pincode" value={pincode} onChange={setPincode} inputMode="numeric" error={pinError} />
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={name.trim() === '' || pinError !== undefined || create.isPending}
          onClick={() =>
            create.mutate(
              { cityId, body: { name: name.trim(), pincode: pincode || null } },
              {
                onSuccess: () => {
                  toast({ tone: 'success', message: `${name.trim()} added` });
                  setName('');
                  setPincode('');
                },
              },
            )
          }
        >
          Add locality
        </ScopeAction>
      </div>
      {create.isError && <ErrorState inline title="The server refused this locality" cause={errorMessage(create.error)} />}
    </Section>
  );
}

/* ── Travel rings ───────────────────────────────────────────────────────────────────────── */

export function TravelZonesPanel({ cityId }: { cityId: number }) {
  const { scope, toast } = useConsole();
  const zones = useTravelZones(cityId);
  const create = useCreateTravelZone();
  const patch = usePatchTravelZone();
  const rings = zones.data ?? [];
  const nextOrdinal = String(rings.reduce((top, z) => Math.max(top, z.zone_ordinal), 0) + 1);
  /* Null until typed: the default follows the rings as they are added, and a cleared field stays cleared. */
  const [ordinal, setOrdinal] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [minKm, setMinKm] = useState('');
  const [maxKm, setMaxKm] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const overlap = create.error instanceof ApiError && create.error.code === 'zone_overlap';
  const shownOrdinal = ordinal ?? nextOrdinal;
  const ordinalError = wholeError(shownOrdinal, 100);
  const minError = minKm === '' || KM.test(minKm) ? undefined : 'Kilometres, one decimal';
  const maxError = maxKm === '' || KM.test(maxKm) ? undefined : 'Kilometres, one decimal';
  const valid = !ordinalError && label.trim() !== '' && minKm !== '' && maxKm !== '' && !minError && !maxError;

  return (
    <Section title="Travel zones">
      <Note>
        Distance bands. What a pandit is paid to travel, not where they may go. A ring a quote has used keeps its bounds —
        retire it and add a successor to move a boundary.
      </Note>
      {zones.isPending && <LoadingState message="Reading travel zones…" />}
      {zones.isError && <ErrorState title="Could not read travel zones" cause={errorMessage(zones.error)} />}
      {zones.isSuccess && rings.length === 0 && <EmptyState icon="signpost" title="No travel zone set" inline />}
      {warnings.map((w) => (
        <StatusChip key={w} tone="warn" label={w} />
      ))}
      <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
        {rings.map((z) => (
          <div key={z.id}>
          <div style={rowStyle}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>
                {z.zone_ordinal}. {z.label}
              </div>
              <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                {z.min_km}–{z.max_km} km
              </div>
            </div>
            <StatusChip tone={z.is_active ? 'success' : 'neutral'} label={z.is_active ? 'active' : 'retired'} />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              ariaLabel={`Rename ${z.label}`}
              onClick={() => setRenamingId(renamingId === z.id ? null : z.id)}
            >
              Rename
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              onClick={() =>
                patch.mutate(
                  { zoneId: z.id, patch: { is_active: !z.is_active } },
                  {
                    onSuccess: () => toast({ tone: 'success', message: `${z.label} ${z.is_active ? 'retired' : 'restored'}` }),
                    onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
                  },
                )
              }
            >
              {z.is_active ? 'Retire' : 'Restore'}
            </ScopeAction>
          </div>
          {renamingId === z.id && <ZoneLabelForm zone={z} onDone={() => setRenamingId(null)} />}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
        <Input label="Ordinal" value={shownOrdinal} onChange={setOrdinal} inputMode="numeric" error={ordinalError} />
        <Input label="Ring label" value={label} onChange={setLabel} />
        <Input label="From km" value={minKm} onChange={setMinKm} inputMode="decimal" error={overlap ? 'Overlaps another ring' : minError} />
        <Input label="To km" value={maxKm} onChange={setMaxKm} inputMode="decimal" error={overlap ? 'Overlaps another ring' : maxError} />
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate(
              { cityId, body: { zone_ordinal: Number(shownOrdinal), label: label.trim(), min_km: minKm, max_km: maxKm } },
              {
                onSuccess: (made) => {
                  setWarnings(made.warnings ?? []);
                  toast({ tone: made.warnings?.length ? 'info' : 'success', message: `${made.zone.label} added` });
                  setOrdinal(null);
                  setLabel('');
                  setMinKm('');
                  setMaxKm('');
                },
              },
            )
          }
        >
          Add ring
        </ScopeAction>
      </div>
      {create.isError && <ErrorState inline title="The server refused this ring" cause={errorMessage(create.error)} />}
    </Section>
  );
}

/** A short IST date for a waitlist row or week: "26 Oct". */
export function istDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' });
}

/**
 * The launch waitlist of one city (admin doc 12 G8). Built from the doc, not a design: the owner
 * waived the design rule for this panel on 2026-10-26. Read-only — joining happens on the website,
 * and the waitlist is notified by the worker when the city goes live, so there is nothing to act on
 * here but the numbers. Phone numbers arrive masked from the server; this never sees one whole.
 */
export function WaitlistPanel({ cityId }: { cityId: number }) {
  const waitlist = useCityWaitlist(cityId);
  const data = waitlist.data;
  const peak = Math.max(1, ...(data?.trend ?? []).map((w) => w.joins));
  const sources = Object.entries(data?.by_source ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <Section title="Launch waitlist">
      <Note>Who asked to be told when this city opens. Everyone pending is notified when it goes live.</Note>
      {waitlist.isPending && <LoadingState message="Reading the waitlist…" />}
      {waitlist.isError && <ErrorState title="Could not read the waitlist" cause={errorMessage(waitlist.error)} />}
      {data && data.total === 0 && <EmptyState icon="users" title="Nobody on the waitlist yet" inline />}
      {data && data.total > 0 && (
        <div style={{ display: 'grid', gap: 'var(--sp-md)', marginTop: 'var(--sp-md)' }}>
          <KV
            rows={[
              ['Signed up', String(data.total)],
              ['Waiting to be told', String(data.pending)],
              ['Already notified', String(data.notified)],
            ]}
          />
          <div>
            <Kicker>Where they came from</Kicker>
            {sources.map(([source, count]) => (
              <div key={source} style={rowStyle}>
                <div style={{ flex: 1 }}>{source}</div>
                <div>{count}</div>
              </div>
            ))}
          </div>
          <div>
            <Kicker>Joins by week</Kicker>
            {data.trend.map((w) => (
              <div key={w.week_start} style={rowStyle} aria-label={`Week of ${istDay(w.week_start)}: ${w.joins}`}>
                <div style={{ width: '5rem' }}>{istDay(w.week_start)}</div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      width: `${(w.joins / peak) * 100}%`,
                      minWidth: w.joins ? 2 : 0,
                      height: 8,
                      background: 'var(--c-accent)',
                      borderRadius: 2,
                    }}
                  />
                </div>
                <div style={{ width: '3rem', textAlign: 'right' }}>{w.joins}</div>
              </div>
            ))}
          </div>
          <div>
            <Kicker>Most recent</Kicker>
            {data.recent.map((r) => (
              <div key={r.id} style={rowStyle}>
                <div style={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>{r.phone_masked}</div>
                <div style={{ color: 'var(--c-ink2)' }}>{r.source}</div>
                <div style={{ color: 'var(--c-ink2)' }}>{istDay(r.joined_at)}</div>
                <StatusChip
                  tone={r.notified_at ? 'success' : 'neutral'}
                  label={r.notified_at ? 'notified' : 'waiting'}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}
